<template>

  <section class="matches">

    <template v-if="hasAny">

      <!-- بازی‌های پیش‌رو و بازی‌های زنده -->
      <template v-if="upcoming.length">

        <h3 class="section-title">

          <span class="dot live-dot"></span>

          بازی‌های پیش‌رو

        </h3>

        <div class="matches-list">

          <article
              v-for="match in upcoming.slice(0, upcomingShown)"
              :key="match.id"
              class="match-row"
              :class="{ live: isLive(match) }"
              @click="$router.push(`/match/${match.id}`)"
          >

            <!-- میزبان -->
            <div
                class="team home-team clickable-team"
                @click.stop="goTeam(match.home)"
            >

              <img
                  v-if="match.home?.logo"
                  :src="match.home.logo"
                  :alt="match.home?.name"
              >

              <span>{{ teamName(match.home?.name) }}</span>

            </div>

            <!-- مرکز: نتیجه/ساعت + تاریخ -->
            <div class="match-center">

              <div class="center-line">

                <!-- زنده -->
                <template v-if="isLive(match)">

                  <strong class="score live-score">

                    {{ match.home?.score ?? 0 }} - {{ match.away?.score ?? 0 }}

                  </strong>

                  <span class="status-dot live-dot">

                    <i></i>

                    زنده

                  </span>

                </template>

                <!-- تمام‌شده -->
                <template v-else-if="match.status === 'FINISHED' || match.status === 'AWARDED'">

                  <strong class="score finished-score">

                    {{ match.home?.score ?? 0 }} - {{ match.away?.score ?? 0 }}

                  </strong>

                  <span class="status-dot finished-dot">پایان</span>

                </template>

                <!-- برنامه‌ریزی‌شده -->
                <template v-else-if="match.status === 'SCHEDULED' || match.status === 'TIMED'">

                  <strong class="kickoff">{{ timeOnly(match) }}</strong>

                </template>

                <!-- به تعویق افتاده/متوقف -->
                <template v-else>

                  <strong class="kickoff">{{ statusLabel(match.status) }}</strong>

                </template>

              </div>

              <div
                  v-if="dateOnly(match) || roundLabel(match.round)"
                  class="when-line"
              >

                <span
                    v-if="roundLabel(match.round)"
                    class="round-chip"
                >{{ roundLabel(match.round) }}</span>

                <span
                    v-if="dateOnly(match)"
                    class="when-date"
                >{{ dateOnly(match) }}</span>

              </div>

            </div>

            <!-- مهمان -->
            <div
                class="team away-team clickable-team"
                @click.stop="goTeam(match.away)"
            >

              <span>{{ teamName(match.away?.name) }}</span>

              <img
                  v-if="match.away?.logo"
                  :src="match.away.logo"
                  :alt="match.away?.name"
              >

            </div>

          </article>

        </div>

        <button
            v-if="upcoming.length > upcomingShown"
            class="more-btn"
            @click="upcomingShown += 10"
        >

          نمایش بیشتر

        </button>

      </template>

      <!-- نتایج -->
      <template v-if="finished.length">

        <h3 class="section-title">

          <span class="dot done-dot"></span>

          نتایج

        </h3>

        <div class="matches-list">

          <article
              v-for="match in finished.slice(0, finishedShown)"
              :key="match.id"
              class="match-row"
              @click="$router.push(`/match/${match.id}`)"
          >

            <!-- میزبان -->
            <div
                class="team home-team clickable-team"
                @click.stop="goTeam(match.home)"
            >

              <img
                  v-if="match.home?.logo"
                  :src="match.home.logo"
                  :alt="match.home?.name"
              >

              <span>{{ teamName(match.home?.name) }}</span>

            </div>

            <!-- مرکز: نتیجه + تاریخ -->
            <div class="match-center">

              <div class="center-line">

                <strong class="score finished-score">

                  {{ match.home?.score ?? 0 }} - {{ match.away?.score ?? 0 }}

                </strong>

                <span class="status-dot finished-dot">پایان</span>

              </div>

              <div
                  v-if="dateOnly(match) || roundLabel(match.round)"
                  class="when-line"
              >

                <span
                    v-if="roundLabel(match.round)"
                    class="round-chip"
                >{{ roundLabel(match.round) }}</span>

                <span
                    v-if="dateOnly(match)"
                    class="when-date"
                >{{ dateOnly(match) }}</span>

              </div>

            </div>

            <!-- مهمان -->
            <div
                class="team away-team clickable-team"
                @click.stop="goTeam(match.away)"
            >

              <span>{{ teamName(match.away?.name) }}</span>

              <img
                  v-if="match.away?.logo"
                  :src="match.away.logo"
                  :alt="match.away?.name"
              >

            </div>

          </article>

        </div>

        <button
            v-if="finished.length > finishedShown"
            class="more-btn"
            @click="finishedShown += 10"
        >

          نمایش بیشتر

        </button>

      </template>

    </template>

    <div
        v-else
        class="empty-state"
    >

      ⚽ بازی‌ای برای این فصل ثبت نشده

    </div>

  </section>

</template>

<script>

import { teamNames, roundLabel } from '@/utils/translations';

const LIVE_STATUSES = ['LIVE', 'IN_PLAY', 'PAUSED'];
const FINISHED_STATUSES = ['FINISHED', 'AWARDED'];
const UPCOMING_STATUSES = ['SCHEDULED', 'TIMED', 'POSTPONED', 'SUSPENDED'];

const STATUS_FA = {
  SCHEDULED: 'برنامه‌ریزی شده',
  TIMED: 'زمان‌بندی شده',
  LIVE: 'زنده',
  IN_PLAY: 'در جریان',
  PAUSED: 'استراحت',
  FINISHED: 'پایان یافته',
  POSTPONED: 'به تعویق افتاد',
  SUSPENDED: 'متوقف شد',
  CANCELLED: 'لغو شد',
  AWARDED: 'پایان یافته'
};

export default {

  name: 'CompetitionMatches',

  props: {

    matches: {

      type: Array,

      default: () => []

    }

  },

  data() {

    return {

      upcomingShown: 10,

      finishedShown: 10

    }

  },

  computed: {

    hasAny() {

      return this.upcoming.length > 0 || this.finished.length > 0;

    },

    // بازی‌های زنده اول، بعد نزدیک‌ترین بازی‌ها
    upcoming() {

      return this.matches
          .filter(m => LIVE_STATUSES.includes(m.status) || UPCOMING_STATUSES.includes(m.status))
          .sort((a, b) => this.isLive(b) - this.isLive(a) ||
              new Date(a.kickoffAt) - new Date(b.kickoffAt));

    },

    finished() {

      return this.matches
          .filter(m => FINISHED_STATUSES.includes(m.status))
          .sort((a, b) => new Date(b.kickoffAt) - new Date(a.kickoffAt));

    }

  },

  methods: {

    isLive(match) {

      return LIVE_STATUSES.includes(match.status);

    },

    // کلیک روی نام تیم → صفحهٔ تیم (نسخهٔ slug کانونیکال، فالبک id)
    goTeam(team) {

      const key = team && (team.slug || team.id);

      if (key) this.$router.push(`/team/${key}`);

    },

    teamName(englishName) {

      return teamNames[englishName] || englishName || 'نامشخص';

    },

    // برچسب فارسی هفته/مرحله — خروجی خالی یعنی نمایش نده
    roundLabel(value) {

      return roundLabel(value);

    },

    statusLabel(status) {

      return STATUS_FA[status] || status;

    },

    // تاریخ شمسی (بدون ساعت) به وقت تهران — خط دومِ وسط کارت
    dateOnly(match) {

      if (!match.kickoffAt) return '';

      try {

        return new Intl.DateTimeFormat(
            'fa-IR-u-ca-persian',
            { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Asia/Tehran' }
        ).format(new Date(match.kickoffAt));

      } catch (e) {

        return '';

      }

    },

    timeOnly(match) {

      if (!match.kickoffAt) return '—';

      try {

        return new Intl.DateTimeFormat(
            'fa-IR',
            { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tehran' }
        ).format(new Date(match.kickoffAt));

      } catch (e) {

        return '—';

      }

    }

  }

}

</script>

<style scoped>

/* ==========================================================
   کارت بازی افقی — دقیقاً هم‌استایل ردیف‌های صفحهٔ مسابقات
   (src/views/Matches.vue → .match-row)
========================================================== */

.matches{

  display:flex;

  flex-direction:column;

  gap:14px;

}

.section-title{

  display:flex;

  align-items:center;

  gap:8px;

  font-size:16px;

  font-weight:700;

  color:#1e293b;

  margin:8px 4px 2px;

}

.dot{

  width:10px;

  height:10px;

  border-radius:50%;

  display:inline-block;

}

.dot.live-dot{

  background:#ef4444;

  animation:pulse 1.5s infinite;

}

.done-dot{

  background:#22c55e;

}

@keyframes pulse{

  0%,100%{ opacity:1; transform:scale(1); }

  50%{ opacity:.5; transform:scale(.8); }

}

/* ظرف ردیف‌ها — معادل .competition-block صفحهٔ مسابقات */
.matches-list{

  background:#fff;

  border-radius:16px;

  box-shadow:0 4px 12px rgba(0,0,0,.06);

  overflow:hidden;

}

/* ─── ردیف بازی: افقی و کم‌ارتفاع، مثل صفحهٔ مسابقات ─── */
.match-row{

  min-height:52px;

  display:grid;

  grid-template-columns:1fr 150px 1fr;

  align-items:center;

  gap:20px;

  padding:8px 20px;

  border-bottom:1px solid #f0f2f5;

  cursor:pointer;

  transition:.18s;

}

.match-row:last-child{

  border-bottom:0;

}

.match-row:hover{

  background:#fbfaff;

}

/* ─── تیم‌ها ─── */
.team{

  display:flex;

  align-items:center;

  gap:10px;

  font-size:14px;

  font-weight:500;

  min-width:0;

}

.clickable-team{

  cursor:pointer;

}

.clickable-team:hover span{

  color:#2563eb;

  text-decoration:underline;

}

.home-team{

  justify-content:flex-start;

}

.away-team{

  justify-content:flex-end;

  text-align:right;

}

.team span{

  white-space:nowrap;

  overflow:hidden;

  text-overflow:ellipsis;

  font-weight:600;

  color:gray;

}

.team img{

  width:25px;

  height:25px;

  object-fit:contain;

  flex-shrink:0;

}

/* ─── مرکز: نتیجه/ساعت + خط تاریخ ─── */
.match-center{

  display:flex;

  flex-direction:column;

  align-items:center;

  justify-content:center;

  min-width:0;

  gap:2px;

}

.center-line{

  display:flex;

  align-items:center;

  justify-content:center;

  gap:8px;

  min-width:0;

}

.kickoff,

.score{

  font-size:16px;

  font-weight:800;

  line-height:1;

}

.finished-score{

  color:#344054;

}

.live-score{

  color:#d92d20;

}

/* نشان وضعیت — همان قرص‌های صفحهٔ مسابقات */
.status-dot{

  display:inline-flex;

  align-items:center;

  gap:4px;

  padding:3px 7px;

  border-radius:20px;

  font-size:10px;

  font-weight:600;

  white-space:nowrap;

}

.status-dot.live-dot{

  color:#d92d20;

  background:#fff1f0;

}

.status-dot.live-dot i{

  width:5px;

  height:5px;

  border-radius:50%;

  background:#d92d20;

}

.status-dot.finished-dot{

  color:#667085;

  background:#f2f4f7;

}

/* خط دوم: هفته/مرحله + تاریخ شمسی */
.when-line{

  display:flex;

  align-items:center;

  justify-content:center;

  flex-wrap:wrap;

  gap:4px;

  font-size:10px;

  color:#98a2b3;

  line-height:1.4;

}

.round-chip{

  background:#f2f4f7;

  color:#475467;

  padding:1px 6px;

  border-radius:6px;

  font-weight:600;

  font-size:10px;

  white-space:nowrap;

}

.when-date{

  white-space:nowrap;

}

/* ─── نمایش بیشتر + حالت خالی ─── */
.more-btn{

  border:none;

  background:#f1f5f9;

  color:#2563eb;

  padding:12px;

  border-radius:14px;

  cursor:pointer;

  font-size:14px;

  font-weight:600;

  transition:.25s;

}

.more-btn:hover{

  background:#dbeafe;

}

.empty-state{

  background:#fff;

  border-radius:18px;

  padding:70px 24px;

  text-align:center;

  color:#64748b;

}

/* ─── موبایل: همان چیدمان افقی صفحهٔ مسابقات ─── */
@media(max-width:768px){

  .match-row{

    grid-template-columns:1fr 85px 1fr;

    gap:4px;

    min-height:64px;

    padding:7px 8px;

  }

  .team{

    font-size:11px;

    gap:5px;

  }

  .team img{

    width:28px;

    height:28px;

  }

  .score,

  .kickoff{

    font-size:14px;

  }

  /* در عرض کم، چیپ هفته جا نمی‌شود — تاریخ کافی است */
  .round-chip{

    display:none;

  }

}

</style>
